import { getApiUrl } from "../config/api";

const VISITOR_KEY = "kissariti_visitor_id";
const SESSION_KEY = "kissariti_session_id";
const LAST_PV_KEY = "kissariti_last_pageview";
const PV_STARTED_KEY = "kissariti_pv_started";

type LastPageview = {
  id: number;
  path: string;
  startedAt: number;
};

function uuid(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getVisitorKey(): string {
  if (typeof localStorage === "undefined") return uuid();
  let key = localStorage.getItem(VISITOR_KEY);
  if (!key) {
    key = uuid();
    localStorage.setItem(VISITOR_KEY, key);
  }
  return key;
}

export function getSessionUuid(): string {
  if (typeof sessionStorage === "undefined") return uuid();
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = uuid();
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function trackApiUrl(): string {
  if (typeof window !== "undefined") {
    const o = window.location.origin;
    if (o === "https://www.kissariti.ma" || o === "https://kissariti.ma") {
      return "/api/analytics_track.php";
    }
  }
  return getApiUrl("ANALYTICS_TRACK");
}

export function statsApiUrl(key: string): string {
  const base =
    typeof window !== "undefined" &&
    (window.location.origin === "https://www.kissariti.ma" ||
      window.location.origin === "https://kissariti.ma")
      ? "/api/analytics_stats.php"
      : getApiUrl("ANALYTICS_STATS");
  return `${base}?key=${encodeURIComponent(key)}`;
}

function getUtmParams(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach(
    (k) => {
      const v = p.get(k);
      if (v) out[k] = v;
    }
  );
  return out;
}

function shouldTrackPath(path: string): boolean {
  if (path.startsWith("/secret-page/")) return false;
  return true;
}

async function postAnalytics(body: Record<string, unknown>): Promise<unknown> {
  try {
    const res = await fetch(trackApiUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

function getLastPageview(): LastPageview | null {
  try {
    const raw = sessionStorage.getItem(LAST_PV_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LastPageview;
  } catch {
    return null;
  }
}

function setLastPageview(pv: LastPageview | null): void {
  if (!pv) {
    sessionStorage.removeItem(LAST_PV_KEY);
    sessionStorage.removeItem(PV_STARTED_KEY);
    return;
  }
  sessionStorage.setItem(LAST_PV_KEY, JSON.stringify(pv));
  sessionStorage.setItem(PV_STARTED_KEY, String(pv.startedAt));
}

export function readCartForAnalytics(): {
  cart: unknown[];
  items_count: number;
  cart_value: number;
} {
  try {
    const raw = localStorage.getItem("cartItems");
    if (!raw) return { cart: [], items_count: 0, cart_value: 0 };
    const items = JSON.parse(raw);
    if (!Array.isArray(items)) return { cart: [], items_count: 0, cart_value: 0 };
    let value = 0;
    for (const item of items) {
      const qty = Number(item.quantity ?? item.quantityPursh ?? 1) || 1;
      const price = Number(item.price ?? item.originPrice ?? 0) || 0;
      value += price * qty;
    }
    return { cart: items, items_count: items.length, cart_value: Math.round(value * 100) / 100 };
  } catch {
    return { cart: [], items_count: 0, cart_value: 0 };
  }
}

export async function analyticsInit(): Promise<void> {
  if (typeof window === "undefined") return;
  const path = window.location.pathname + window.location.search;
  if (!shouldTrackPath(path)) return;

  const utm = getUtmParams();
  await postAnalytics({
    action: "init",
    session_uuid: getSessionUuid(),
    visitor_key: getVisitorKey(),
    path,
    landing_path: path,
    referrer: document.referrer || null,
    ...utm,
  });
}

export async function analyticsHeartbeat(): Promise<void> {
  await postAnalytics({
    action: "heartbeat",
    session_uuid: getSessionUuid(),
    visitor_key: getVisitorKey(),
  });
}

export async function analyticsPageView(path: string, pageTitle?: string): Promise<void> {
  if (!shouldTrackPath(path)) return;

  const prev = getLastPageview();
  const now = Date.now();
  const body: Record<string, unknown> = {
    action: "pageview",
    session_uuid: getSessionUuid(),
    visitor_key: getVisitorKey(),
    path,
    page_title: pageTitle || document.title,
  };

  if (prev) {
    body.previous_pageview_id = prev.id;
    body.duration_seconds = Math.max(1, Math.round((now - prev.startedAt) / 1000));
    body.is_exit = false;
  }

  const res = (await postAnalytics(body)) as { pageview_id?: number } | null;
  if (res?.pageview_id) {
    setLastPageview({ id: res.pageview_id, path, startedAt: now });
  }
}

export async function analyticsSyncCart(path?: string): Promise<void> {
  const p =
    path ??
    (typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : "/");
  if (!shouldTrackPath(p.split("?")[0])) return;

  const { cart, items_count, cart_value } = readCartForAnalytics();
  await postAnalytics({
    action: "cart",
    session_uuid: getSessionUuid(),
    visitor_key: getVisitorKey(),
    cart,
    items_count,
    cart_value,
    last_page: p,
  });
}

export async function analyticsExit(): Promise<void> {
  if (typeof window === "undefined") return;
  const exitPath = window.location.pathname + window.location.search;
  if (!shouldTrackPath(exitPath.split("?")[0])) return;
  if (exitPath.includes("order-confirmation")) return;

  const prev = getLastPageview();
  const now = Date.now();
  const { cart, items_count, cart_value } = readCartForAnalytics();

  const body: Record<string, unknown> = {
    action: "exit",
    session_uuid: getSessionUuid(),
    visitor_key: getVisitorKey(),
    exit_path: exitPath,
    path: exitPath,
    cart,
    items_count,
    cart_value,
  };

  if (prev) {
    body.previous_pageview_id = prev.id;
    body.duration_seconds = Math.max(1, Math.round((now - prev.startedAt) / 1000));
  }

  await postAnalytics(body);
  setLastPageview(null);
}

export async function analyticsOrderConverted(orderNumber: string): Promise<void> {
  await postAnalytics({
    action: "convert",
    session_uuid: getSessionUuid(),
    visitor_key: getVisitorKey(),
    order_number: orderNumber,
  });
}
